/**
 * Centralized API Configuration Management
 * Handles environment-specific API endpoints for staging and production
 */

// Environment configurations
const ENVIRONMENTS = {
  STAGING: 'staging',
  PRODUCTION: 'production',
  DEVELOPMENT: 'development',
};

// API Endpoints Configuration
const API_ENDPOINTS = {
  staging: {
    AUTH: 'https://namasvi-auth-dev-226556767471.asia-south1.run.app',
    API: 'https://myapi-dev-226556767471.asia-south1.run.app',
    NOTIFICATIONS: 'https://namasvi-notifications-dev-226556767471.asia-south1.run.app',
    SUPPORT: 'https://support-dev-226556767471.asia-south1.run.app',
    AUTO_REPLY: process.env.REACT_APP_AUTO_REPLY_API_URL || 'http://localhost:8081',
    SCHEDULING: process.env.REACT_APP_SCHEDULING_DOMAIN || 'https://namasvi-scheduling-dev-226556767471.asia-south1.run.app',
    REEL_SERVICE: 'https://namasvi-reel-dev-226556767471.asia-south1.run.app',
  },
  production: {
    AUTH: 'https://namasvi-auth-226556767471.asia-south1.run.app',
    API: 'https://namasvi-api-226556767471.asia-south1.run.app',
    NOTIFICATIONS: 'https://namasvi-notifications-226556767471.asia-south1.run.app',
    SUPPORT: 'https://support-api-226556767471.asia-south1.run.app',
    AUTO_REPLY: process.env.REACT_APP_AUTO_REPLY_API_URL || 'http://localhost:8081',
    SCHEDULING: process.env.REACT_APP_SCHEDULING_DOMAIN || 'https://namasvi-scheduling-226556767471.asia-south1.run.app',
    REEL_SERVICE: 'https://namasvi-reel-226556767471.asia-south1.run.app',
  },
};

/**
 * Get current environment from multiple sources
 * Priority: localStorage > environment variable > default
 */
const getCurrentEnvironment = () => {
  // Check localStorage first (user preference)
  const storedEnv = localStorage.getItem('NAMASVI_ENVIRONMENT');
  if (storedEnv && Object.values(ENVIRONMENTS).includes(storedEnv)) {
    return storedEnv;
  }

  // Check process.env for build-time configuration
  if (process.env.REACT_APP_ENVIRONMENT) {
    return process.env.REACT_APP_ENVIRONMENT;
  }

  // Default to production
  return ENVIRONMENTS.PRODUCTION;
};

/**
 * Set environment preference and store in localStorage
 */
const setEnvironment = (env) => {
  if (!Object.values(ENVIRONMENTS).includes(env)) {
    console.error(`Invalid environment: ${env}`);
    return false;
  }
  localStorage.setItem('NAMASVI_ENVIRONMENT', env);
  return true;
};

/**
 * Get API configuration for current environment
 */
const getAPIConfig = () => {
  const currentEnv = getCurrentEnvironment();
  return API_ENDPOINTS[currentEnv] || API_ENDPOINTS.production;
};

/**
 * Get specific API endpoint
 */
const getEndpoint = (service) => {
  const config = getAPIConfig();
  const endpoint = config[service];

  if (!endpoint) {
    console.warn(`Endpoint not found for service: ${service}`);
  }

  return endpoint;
};

/**
 * API Service URLs - Easy access methods
 */
const API_URLs = {
  // Auth Service
  AUTH_BASE: () => getEndpoint('AUTH'),
  AUTH_VALIDATE: () => `${getEndpoint('AUTH')}/auth/validate`,
  AUTH_FACEBOOK: () => `${getEndpoint('AUTH')}/auth/facebook`,

  // Main API Service
  API_BASE: () => getEndpoint('API'),
  EVENTS: () => `${getEndpoint('API')}/events`,
  CATEGORIES: () => `${getEndpoint('API')}/categories`,
  VENUES: () => `${getEndpoint('API')}/venues`,
  PARTICIPANTS: () => `${getEndpoint('API')}/participants`,
  SPONSORS: () => `${getEndpoint('API')}/sponsors`,
  WALLETS: () => `${getEndpoint('API')}/wallets`,
  VIDEO_PACKAGES: () => `${getEndpoint('API')}/video-packages`,
  REFERRALS: () => `${getEndpoint('API')}/referrals`,
  HASHTAGS: () => `${getEndpoint('API')}/hashtags`,

  // Referral Admin Service V2
  REFERRAL_ADMIN_STATS: () => `${getEndpoint('API')}/api/v2/referral/admin/stats`,
  REFERRAL_ADMIN_PERCENTAGE: () => `${getEndpoint('API')}/api/v2/referral/admin/percentage`,
  REFERRAL_ADMIN_TRANSACTIONS: () => `${getEndpoint('API')}/api/v2/referral/admin/transactions`,
  REFERRAL_ADMIN_USERS: () => `${getEndpoint('API')}/api/v2/referral/admin/users`,
  REFERRAL_ADMIN_USER_CHAIN: (userId) => `${getEndpoint('API')}/api/v2/referral/admin/users/${userId}/chain`,
  REFERRAL_ADMIN_SPONSORS_WITH_REFERRALS: () => `${getEndpoint('API')}/api/v2/referral/admin/sponsors-with-referrals`,
  REFERRAL_VALIDATE_CODE: () => `${getEndpoint('API')}/api/v2/referral/validate-code`,

  // User Onboarding & Activation
  USER_ACTIVATION_REQUESTS: () => `${getEndpoint('API')}/api/v2/admin/user-activation/requests`,
  USER_ACTIVATION_APPROVE: () => `${getEndpoint('API')}/api/v2/admin/user-activation/approve`,
  USER_INFORMATION: () => `${getEndpoint('API')}/api/v2/userinformation`,

  // Notification Service
  NOTIFICATIONS_BASE: () => getEndpoint('NOTIFICATIONS'),
  NOTIFICATIONS: () => `${getEndpoint('NOTIFICATIONS')}/notifications`,

  // Support Service
  SUPPORT_BASE: () => getEndpoint('SUPPORT'),
  SUPPORT_TICKETS: () => `${getEndpoint('SUPPORT')}/tickets`,
  SUPPORT_FAQs: () => `${getEndpoint('SUPPORT')}/faqs`,

  // Auto-Reply / Automation Service
  AUTO_REPLY_BASE: () => getEndpoint('AUTO_REPLY'),
};

/**
 * Get all current configuration
 */
const getConfig = () => ({
  environment: getCurrentEnvironment(),
  endpoints: getAPIConfig(),
  allEnvironments: ENVIRONMENTS,
});

/**
 * Get the Namasvi main API base URL for the current environment.
 * All automation endpoints (/api/v2/automation/*) are served by the Namasvi
 * backend — the browser never calls insta-auto-reply directly.
 */
const getApiBaseUrl = () => getEndpoint('API');
const getSchedulingBaseUrl = () => getEndpoint('SCHEDULING');
const getReelServiceBaseUrl = () => getEndpoint('REEL_SERVICE');

export {
  ENVIRONMENTS,
  API_ENDPOINTS,
  getCurrentEnvironment,
  setEnvironment,
  getAPIConfig,
  getEndpoint,
  API_URLs,
  getConfig,
  getApiBaseUrl,
  getSchedulingBaseUrl,
  getReelServiceBaseUrl,
};
